/**
 * Textos PT-BR da chamada de vídeo (atendente): motivos, formas de envio, modos de entrada,
 * validade do link e mensagens de erro. Fonte: PRD (US-3, US-4, US-7, US-10) e design.md.
 */

/** Motivo de indisponibilidade de uma forma de envio (`/options`). */
export const OPTION_REASON_TEXT = Object.freeze({
    NO_CONTACT: 'Sem contato, só por link',
    CONTACT_WITHOUT_APP: 'Contato não tem o app instalado',
    APP_NOT_CONFIGURED: 'App não configurado na conta',
    TEMPLATE_PENDING: 'Aguardando aprovação do modelo de mensagem pela Meta',
    TEMPLATE_REJECTED: 'Modelo de mensagem recusado pela Meta',
    TEMPLATE_MISSING: 'Modelo de mensagem ainda não criado neste canal',
    CHANNEL_WINDOW_CLOSED: 'Janela de mensagens encerrada neste canal',
});

/** Motivos de bloqueio de uma chamada nova (`blockedReason` das opções), em PT-BR (VCALL-12, 77). */
export const BLOCKED_REASON_TEXT = Object.freeze({
    AGENT_IN_CALL: 'Você já está em uma chamada',
    CONTACT_IN_CALL: 'Já existe uma chamada de vídeo em andamento com este contato',
    MINUTES_EXHAUSTED: 'Limite de minutos de vídeo do mês atingido',
});

export const NO_CHANNEL_AVAILABLE_TEXT = 'Nenhum canal disponível para este contato';

/** Tipo de canal exibido ao lado do nome. */
export const CHANNEL_TYPE_LABEL = Object.freeze({
    WHATSAPP: 'WhatsApp',
    INSTAGRAM: 'Instagram',
    TELEGRAM: 'Telegram',
    WEBCHAT: 'Webchat',
    EMAIL: 'E-mail',
    TIKTOK: 'TikTok',
    APP_CONNECT: 'App',
});

export const channelTypeLabel = (type) => CHANNEL_TYPE_LABEL[type] ?? type ?? '';

export const DELIVERY_METHOD_LABEL = Object.freeze({
    APP: 'Pelo app',
    CHANNEL: 'Por canal',
    LINK: 'Link copiado',
});

export const ENTRY_MODE_LABEL = Object.freeze({
    OPEN: 'Aberto',
    APPROVAL: 'Com aprovação',
});

/** Motivo de encerramento exibido ao atendente. */
export const END_REASON_TEXT = Object.freeze({
    AGENT_HANGUP: 'Você encerrou a chamada',
    GUEST_HANGUP: 'O contato saiu da chamada',
    MINUTES_LIMIT: 'Limite de minutos de vídeo do mês atingido',
    MEETING_LIMIT: 'A reunião chegou à duração máxima do plano',
    RECONNECT_TIMEOUT: 'A conexão caiu e não voltou em 2 minutos',
    NO_ONE_JOINED: 'Ninguém entrou na chamada',
    LINK_EXPIRED: 'O link expirou',
    ADDON_DISABLED: 'Chamadas de vídeo desativadas na conta',
    APP_DECLINED: 'O contato recusou a chamada no app',
    APP_BUSY: 'O contato está ocupado',
    APP_NO_ANSWER: 'O contato não respondeu no app',
    APP_UNAVAILABLE: 'O app do contato está indisponível',
    APP_DELIVERY_FAILED: 'O app do contato está indisponível',
    ERROR: 'A chamada foi encerrada por um erro',
});

export const endReasonText = (endReason) => END_REASON_TEXT[endReason] ?? 'Chamada encerrada';

/** Erros da criação que viram aviso (toast); os demais aparecem no próprio modal. */
export const START_ERROR_TEXT = Object.freeze({
    VIDEO_CALL_DISABLED: 'Chamadas de vídeo temporariamente indisponíveis',
    VIDEO_CAPACITY_EXHAUSTED: 'Capacidade de chamadas esgotada, tente em instantes',
    MEDIA_SERVER_UNAVAILABLE: 'Servidor de vídeo indisponível, tente em instantes',
    DELIVERY_OPTION_UNAVAILABLE: 'A forma de envio escolhida deixou de estar disponível. Escolha outra.',
    DELIVERY_FAILED: 'Não foi possível enviar o link. Escolha outra forma de envio.',
    INVALID_LINK_TTL: 'Validade fora dos limites permitidos',
    TRANSCRIPTION_NOT_AVAILABLE: 'A transcrição não está disponível no plano desta conta.',
});

/**
 * Duração em minutos por extenso: 30 → "30 minutos", 60 → "1 hora", 1440 → "1 dia", 10080 → "7 dias".
 * @param {number} minutes
 */
export function formatMinutes(minutes) {
    const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
    if (minutes % 1440 === 0) return plural(minutes / 1440, 'dia', 'dias');
    if (minutes % 60 === 0) return plural(minutes / 60, 'hora', 'horas');
    return plural(minutes, 'minuto', 'minutos');
}

/**
 * Data/hora PT-BR curta: "07/10/2026 12:00".
 * @param {string|Date|null} value
 */
export function formatDateTime(value) {
    if (!value) return '';
    const date = new Date(value);
    return `${date.toLocaleDateString('pt-BR')} ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

/** Escala da pesquisa de qualidade (nota 1 a 5). */
export const QUALITY_LABELS = Object.freeze(['Péssimo', 'Ruim', 'Regular', 'Bom', 'Excelente']);
